var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { DocumentTypeEnum, PrimaryDocumentTypes } from '@workalizer/shared';
import { formatDuration, getWeekPeriods } from '@workalizer/shared/utils';
import { differenceInWeeks, format } from 'date-fns';
export function groupActivitiesByWeek(activities, dateFrom, dateTo) {
    var weeksDifferent = differenceInWeeks(dateTo, dateFrom);
    if (weeksDifferent <= 4)
        return activities.map(function (activity) { return ({
            date: format(new Date(activity.date), 'MMM d'),
            count: activity.count
        }); });
    var dataWeekPeriods = getWeekPeriods(dateFrom, dateTo);
    var dataWithWeekPeriod = dataWeekPeriods.reduce(function (acc, d) {
        var count = activities
            .filter(function (activity) {
            var date = new Date(activity.date);
            return date >= d.dateFrom && date < d.dateTo;
        })
            .reduce(function (sum, activity) { return sum + activity.count; }, 0);
        var formattedDate = format(d.dateFrom, 'MMM d') + ' - ' + format(d.dateTo, 'MMM d');
        acc.push({
            date: formattedDate,
            count: count
        });
        return acc;
    }, []);
    return dataWithWeekPeriod;
}
export var getDocumentType = function (type) {
    var _a;
    return (_a = PrimaryDocumentTypes[type]) !== null && _a !== void 0 ? _a : DocumentTypeEnum.OTHER;
};
export var formatMeetingsList = function (meetings) {
    return meetings
        .sort(function (a, b) { return b.durationSeconds - a.durationSeconds; })
        .map(function (meeting) { return ({
        title: meeting.meetingGuests
            .map(function (guest) { return guest.name || guest.email; })
            .join(', '),
        duration: formatDuration(meeting.durationSeconds, 'seconds')
    }); });
};
export var calculateMeetingsStats = function (meetings) { return ({
    count: meetings.length,
    durationSeconds: meetings.reduce(function (acc, meeting) { return acc + meeting.durationSeconds; }, 0)
}); };
export var getCompanyInternalRecurring = function (meetings) {
    var companyInternalRecurringMeetings = meetings.filter(function (mg) { var _a; return !mg.isExternalParticipantsInvolved && ((_a = mg === null || mg === void 0 ? void 0 : mg.calendar) === null || _a === void 0 ? void 0 : _a.isRecurring); });
    var meetingsList = Object.values(companyInternalRecurringMeetings.reduce(function (acc, meeting) {
        var _a;
        var calendarId = meeting.calendarId;
        if (!acc[calendarId]) {
            acc[calendarId] = {
                title: ((_a = meeting.calendar) === null || _a === void 0 ? void 0 : _a.title) || '',
                durationSeconds: 0
            };
        }
        acc[calendarId].durationSeconds += meeting.durationSeconds;
        return acc;
    }, {}))
        .sort(function (a, b) { return b.durationSeconds - a.durationSeconds; })
        .map(function (meeting) { return ({
        title: meeting.title,
        duration: formatDuration(meeting.durationSeconds, 'seconds')
    }); });
    return __assign(__assign({}, calculateMeetingsStats(companyInternalRecurringMeetings)), { meetingsList: meetingsList });
};
export var getCompanyInternalOccasional = function (meetings) {
    var companyInternalOccasionalMeetings = meetings.filter(function (mg) { var _a; return !mg.isExternalParticipantsInvolved && !((_a = mg === null || mg === void 0 ? void 0 : mg.calendar) === null || _a === void 0 ? void 0 : _a.isRecurring); });
    var meetingsList = formatMeetingsList(companyInternalOccasionalMeetings);
    return __assign(__assign({}, calculateMeetingsStats(companyInternalOccasionalMeetings)), { meetingsList: meetingsList });
};
export var getWithExternalParticipants = function (meetings) {
    var withExternalParticipantsMeetings = meetings.filter(function (mg) { return mg.isExternalParticipantsInvolved; });
    var meetingsList = formatMeetingsList(withExternalParticipantsMeetings);
    return __assign(__assign({}, calculateMeetingsStats(withExternalParticipantsMeetings)), { meetingsList: meetingsList });
};
var averageMeetingsTypesItem = function (item, teamSize) {
    var divisor = Math.max(teamSize, 1);
    return {
        count: Math.round(item.count / divisor),
        durationSeconds: Math.round(item.durationSeconds / divisor)
    };
};
export var sumMeetingsTypesAnalyticData = function (memberData) {
    var emptyItem = function () { return ({
        count: 0,
        durationSeconds: 0,
        meetingsList: []
    }); };
    return memberData.reduce(function (acc, data) { return ({
        companyInternalRecurring: __assign(__assign({}, acc.companyInternalRecurring), { count: acc.companyInternalRecurring.count +
                data.companyInternalRecurring.count, durationSeconds: acc.companyInternalRecurring.durationSeconds +
                data.companyInternalRecurring.durationSeconds }),
        companyInternalOccasional: __assign(__assign({}, acc.companyInternalOccasional), { count: acc.companyInternalOccasional.count +
                data.companyInternalOccasional.count, durationSeconds: acc.companyInternalOccasional.durationSeconds +
                data.companyInternalOccasional.durationSeconds }),
        withExternalParticipants: __assign(__assign({}, acc.withExternalParticipants), { count: acc.withExternalParticipants.count +
                data.withExternalParticipants.count, durationSeconds: acc.withExternalParticipants.durationSeconds +
                data.withExternalParticipants.durationSeconds })
    }); }, {
        companyInternalRecurring: emptyItem(),
        companyInternalOccasional: emptyItem(),
        withExternalParticipants: emptyItem()
    });
};
export var toTeamAverageMeetingsTypes = function (data, teamSize) { return ({
    companyInternalRecurring: averageMeetingsTypesItem(data.companyInternalRecurring, teamSize),
    companyInternalOccasional: averageMeetingsTypesItem(data.companyInternalOccasional, teamSize),
    withExternalParticipants: averageMeetingsTypesItem(data.withExternalParticipants, teamSize)
}); };
export var averageMeetingsTypesTeamAverageFromMembers = function (memberData, teamSize) {
    return toTeamAverageMeetingsTypes(sumMeetingsTypesAnalyticData(memberData), teamSize);
};
